/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Receipt, Stethoscope, Loader2 } from "lucide-react";
import { ProFactures } from "./Factures";

/** Actes (présentiel / TMK) + téléexpertise du patient — fiche praticien. */
export function ActesCard({ patientId, refreshKey }) {
  const [actes, setActes] = useState([]);
  const [nomen, setNomen] = useState(null);
  const [te, setTe] = useState(null);
  const [code, setCode] = useState("");
  const [medecin, setMedecin] = useState("");
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/telesoin/actes?patient_id=${patientId}`).then(({ data }) => setActes(data)).catch(() => null);
    api.get(`/telesoin/teleexpertises?patient_id=${patientId}`).then(({ data }) => setTe(data)).catch(() => null);
  }, [patientId]);
  useEffect(() => { load(); api.get("/telesoin/nomenclature").then(({ data }) => { setNomen(data); setCode((c) => c || data.actes[0]?.code || ""); }).catch(() => null); }, [load, refreshKey]);
  const [factKey, setFactKey] = useState(0);
  const addActe = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telesoin/actes", { patient_id: patientId, code, mode: "presentiel" }); toast.success(data.facture ? `Acte enregistré · facture ${data.facture.numero} émise` : "Acte présentiel enregistré"); load(); setFactKey((k) => k + 1); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const addTe = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telesoin/teleexpertises", { patient_id: patientId, medecin_requis: medecin.trim(), motif: motif.trim() }); toast.success(`Téléexpertise enregistrée · ${data.restantes} restante(s) cette année`); setMedecin(""); setMotif(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div data-testid="actes-card" className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold inline-flex items-center gap-1"><Receipt size={14} /> Actes & facturation ({nomen?.regime_nom || "…"})</h3>
        <span className="text-[11px] text-[#0A1426]/50">{actes.length} acte(s)</span>
      </div>
      <div className="max-h-40 overflow-y-auto flex flex-col divide-y divide-[#F5F3EE] mb-3">
        {actes.length === 0 && <div className="text-[12px] text-[#0A1426]/60">Aucun acte enregistré.</div>}
        {actes.map((a) => (
          <div key={a.id} data-testid={`acte-${a.id}`} className="py-1.5 flex items-center justify-between gap-2 text-[12px] text-[#0A1426]">
            <span>{new Date(a.date).toLocaleDateString("fr-FR")} · {a.libelle}</span>
            <span className={`shrink-0 font-bold px-2 py-0.5 rounded-full ${a.mode === "distance" ? "bg-[#3a8acb]/15 text-[#2a6a9b]" : "bg-[#F5F3EE]"}`}>{a.lettre_cle} {a.coefficient} · {Number(a.montant ?? a.montant_eur).toFixed(2)} {a.devise || "€"}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <select data-testid="acte-code-select" value={code} onChange={(e) => setCode(e.target.value)} className="flex-1 h-10 px-3 rounded-full border border-[#E4E0D5] text-[12px] text-[#0A1426] bg-white">
          {(nomen?.actes || []).map((a) => <option key={a.code} value={a.code}>{`${a.libelle} (${a.lettre} ${a.coefficient})`}</option>)}
        </select>
        <Button data-testid="acte-add-btn" disabled={busy} onClick={addActe} className="h-10 rounded-full bg-[#0A1426] text-white text-[12px] font-bold px-3"><Stethoscope size={14} className="mr-1" /> Présentiel</Button>
      </div>
      <ProFactures patientId={patientId} refreshKey={factKey + refreshKey} />
      {te?.disponible && <div className="rounded-2xl bg-[#F5F3EE] p-3">
        <div className="flex items-center justify-between text-[12px] text-[#0A1426] mb-2">
          <b>Téléexpertise (TE · {te.montant} €)</b>
          <span data-testid="te-restantes">{te ? `${te.restantes}/${te.plafond} restante(s) en ${new Date().getFullYear()}` : "…"}</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <input data-testid="te-medecin-input" value={medecin} onChange={(e) => setMedecin(e.target.value)} placeholder="Médecin requis (ex. Dr Neurologue)" className="h-10 px-3 rounded-full border border-[#E4E0D5] text-[12px] text-[#0A1426] bg-white" />
          <input data-testid="te-motif-input" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif de la demande d'avis" className="h-10 px-3 rounded-full border border-[#E4E0D5] text-[12px] text-[#0A1426] bg-white" />
          <Button data-testid="te-add-btn" disabled={busy || medecin.trim().length < 2 || motif.trim().length < 3 || te?.restantes === 0} onClick={addTe} className="h-10 rounded-full bg-[#2BA3A0] text-white text-[12px] font-bold">
            {busy ? <Loader2 className="animate-spin" size={14} /> : te?.restantes === 0 ? "Plafond annuel atteint (2/patient/an)" : "Demander une téléexpertise"}
          </Button>
        </div>
        {te?.items?.length > 0 && <div className="mt-2 text-[11px] text-[#0A1426]/70">{te.items.map((i) => <div key={i.id} data-testid={`te-${i.id}`}>{new Date(i.date).toLocaleDateString("fr-FR")} · {i.medecin_requis} · {i.motif}</div>)}</div>}
      </div>}
    </div>
  );
}
